import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { Node, Transforms, Editor  }  from 'slate';
import { useSlate  }  from 'slate-react';
import clsx from 'clsx';
import styled from 'styled-components';
import {
  Media,
  Button, 
  Collapse,
  FormGroup,
  Input,
  Label
} from 'reactstrap';

import UpgradePlan from 'components/Shared/UpgradePlan'
import { useEditorState, useEditorActions } from '../../editor-context'
import { getNodesByType } from '../../Canvas/TratoFormat'
import { truncate } from 'utils'
import { useProfileAbility } from 'context/profile'
import { isAlignBlock } from 'components/Editor/Canvas/TratoFormat/editor-utils'
// import Input from 'react-select/src/components/input';

const TransparentInput = styled.input`
  background: none;
  color: black;
  border: 0;
`

export function Questions() {
    const {t} = useTranslation()
    const profileAbility = useProfileAbility();
    const canSetQuestions = profileAbility.can('SET_QUESTIONS'); // can SET_QUESTIONS
    const { document: doc, mode } = useEditorState();
    const { updateDocument } = useEditorActions();
    const { control, register, handleSubmit, watch, reset, setValue, getValues } = useFormContext({
        defaultValues: {
          questions: doc.questions
        }
    });
    const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: 'questions', // unique name for your Field Array
      // keyName: "id", default to "id", you can change the key name
    });

    const handleSave = useCallback(() => {
      setImmediate(() => {
        handleSubmit(data => { updateDocument({
          questions: data.questions ? data.questions : []
        }) })()
      })
    }, [handleSubmit, updateDocument])

    const handleAddQuestion = () => {
      append({ content:  t('editor.question.new_name'), answers: [] })
      handleSave()
    } 

    const handleRemoveQuestion = useCallback((questionIndex) => {
      remove(questionIndex)
      handleSave()
    }, [handleSave, remove])

    useEffect(() => {
      reset({questions: doc.questions})
    }, [])
   

    if (!canSetQuestions) {
      return <UpgradePlan inverted />
    }


    return (  
      <div className="sidebar-content">
        <div className="sidebar-content-inner">
          <div className="sidebar-questions-content">
            {fields.map((field, index) => (
              <QuestionItem 
                key={field.id} 
                name={`questions[${index}]`} 
                index={index} 
                question={field} 
                control={control}
                register={register} 
                onSubmit={handleSave}
                onRemove={handleRemoveQuestion}
              />
            ))}
            {
              !doc.questions.length && 
              <div className="questions-empty"></div>
            } 
          </div>
        </div>
        <div className="add-new-question text-center">
            <Button color="light" data-action="add-question" onClick={handleAddQuestion}>
              <i className="fas fa-plus"></i> {t('editor.question.add')}
            </Button>
        </div>
      </div>
    )
}

function QuestionItem({question, name, register, control, index, onSubmit, onRemove}) {
  const {t} = useTranslation()
  const [isOpen, setIsOpen] = useState(false);
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: `${name}.answers`, // unique name for your Field Array
    // keyName: "id", default to "id", you can change the key name
  });

  const handleAddAnswer = () => {
    append({ content: t('editor.question.answers_new_name'), actions: [] })
    onSubmit()
  }

  const handleRemoveAnswer = (answerIndex) => {
    remove(answerIndex)
    onSubmit()
  }
  const handleRemove = () => {
    onRemove(index)
  }

  
  const toggle = () => setIsOpen(open => !open);

  return (
    <div className="questions-item-wrapper">
      <Media className="text-dark d-flex align-items-center p-3 border" onClick={toggle}>
          <Media className="questions-item-content font-weight-bold" body>
              <span className="question-text">{question.content}{' '}</span>
              <span className="answer-count">({question.answers.length})</span>
          </Media>
          <Media className="questions-item-caret p-2">
              <i className={`pointer fas fa-${isOpen ? 'chevron-down' : 'chevron-up'}`}></i>
          </Media>
      </Media>
      <Collapse isOpen={isOpen}>
        {/* {isOpen && */}
        <div className={clsx('questions-item-detail bg-light-contrast p-2')}>
          <FormGroup className="form-group d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Label className="text-dark mr-2 mb-0 font-weight-bold">{t('editor.question.name')}:</Label>
              <TransparentInput size="sm" name={`${name}.content`} onBlur={onSubmit} ref={register()} defaultValue={question.content} />
            </div>
            <span className="text-danger" href="#" data-action="delete-question" onClick={handleRemove}>{t('editor.question.remove')}</span>
          </FormGroup>
        <FormGroup className="form-group">
          <Label className="text-dark font-weight-bold">{t('editor.question.answers')}:</Label>
            <div className="sidebar-answers-list">
                {
                  fields.map((field, index) => (
                    <AnswerItem 
                      key={field.id} 
                      answer={field} 
                      name={`${name}.answers[${index}]`} 
                      index={index} 
                      question={field} 
                      register={register} 
                      control={control}
                      onSubmit={onSubmit}
                      onRemove={handleRemoveAnswer}
                    />
                  ))
                }
                {
                  !question.answers.length && 
                  <div className="answers-empty text-dark">
                    {t('editor.question.answers_empty')}
                  </div>
                } 
            </div>
          </FormGroup>
          <div className="add-new-answer text-center">
            <Button color="light-contrast" className="text-dark" size="sm" href="#" data-action="add-answer" onClick={handleAddAnswer}>
              <i className="fas fa-plus"></i> {t('editor.question.answers_add')}
            </Button>
          </div>
        </div>
        {/* } */}
      </Collapse>
   
    </div>
  )
}

function AnswerItem({ answer,  name, register, control, index, onSubmit, onRemove }) {
  const {t} = useTranslation()
  const [isOpen, setIsOpen] = useState(false);
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: `${name}.actions`, // unique name for your Field Array
    // keyName: "id", default to "id", you can change the key name
  });

  const handleAddAction = () => {
    append({ action: 'hide' })
    onSubmit()
  }

  const handleRemoveAction = (actionIndex) => {
    remove(actionIndex)

    onSubmit()
  }
  const handleRemove = () => {
    onRemove(index)
  }
  
  const toggle = () => setIsOpen(open => !open);

  return (
    <div className="answers-item">
      <Media className="text-dark d-flex align-items-center" >
          <Media className="d-flex" body>
            <span className="answer-bullet mr-1">•</span>
            <TransparentInput size="sm" name={`${name}.content`} onBlur={onSubmit} ref={register()} defaultValue={answer.content} />
          </Media>
          <Media className="d-flex align-items-center">
            <span href="#" className="answer-item-caret text-dark pointer" onClick={toggle}>
              <i className={`fas fa-${isOpen ? 'minus' : 'plus'}`} />
            </span>
            <span href="#" className="answer-item-delete ml-2 text-danger pointer" onClick={handleRemove}>
              <i className="fas fa-times"></i>
            </span>
          </Media>
      </Media>
      <Collapse isOpen={isOpen}>
        {/* {isOpen && */}
      <div className={clsx('answer-item-detail')}>
        <FormGroup className="form-group p-2">
          <Label className="text-dark font-weight-bold">{t('editor.question.answers_actions')}:</Label>
          <div className="answer-action-list">
              {
                fields.map((field, index) => (
                  <ActionItem 
                    action={field}
                    key={field.id} 
                    name={`${name}.actions[${index}]`} 
                    index={index} 
                    register={register} 
                    control={control}
                    onSubmit={onSubmit}
                    onRemove={handleRemoveAction}
                  />
                ))
              } 
              {
                !answer.actions.length && 
                <div className="actions-empty text-dark text-center">
                  {t('editor.question.answers_empty_actions')}
                </div>
              } 
          </div>
        </FormGroup>
      
        <div className="add-new-action text-dark text-center">
          <Button className="text-dark" size="sm" color="light-contrast" href="#" data-action="add-action" onClick={handleAddAction}>
            <i className="fas fa-plus"></i> {t('editor.question.answers_add_actions')}
          </Button>
        </div>
      </div>
        {/* } */}
      </Collapse>
    </div>
  )
}

function getKey(type) {
  if (type === 'static-header') return 'header';
  if (type === 'table-wrap') return 'table';

  return type;
}

function ActionItem({ action, name, register, control, index, onSubmit, onRemove  }) {
  const {t} = useTranslation()
  const editor = useSlate();
  const { document: doc, mode } = useEditorState();
  // const type = 'header';
  const type = useWatch({
    control,
    name: `${name}.element`
  })

  // const elements = [];
  const elements = useMemo(() => {
    let nodes = [
      ...getNodesByType(editor, type, { mode: 'highest' }),
    ];

    if (type === 'header') {
      nodes = [
        ...nodes,
        ...getNodesByType(editor, 'static-header', { mode: 'highest' })
      ]
    }

    return nodes.map(([elementNode, elLocation], index) => {
      const el = isAlignBlock(elementNode.type) ? elementNode.children[0] : elementNode;
      const str = Node.string(el);

      const text = str.length ? str : `${t(`editor.question.answers_action_${getKey(el.type)}`)} ${index + 1}`;
      return {
        _id: el._id,
        text, 
        type: el.type,
        location: elLocation
      }
    })
  }, [editor, type, t])

  const attachments = useMemo(() => {
    return doc.participants.flatMap((participant) => {
      return participant.attachments ?? []
    });
  }, [doc.participants])

  const handleRemove = () => {
    onRemove(index)
  }

  return (
    <div className="answer-action-item d-flex align-items-center">
        <span className="answer-action-bullet text-dark">-</span>
        <input type="hidden" name={`${name}.action`} defaultValue={action.action} ref={register()} />
        <Input className="ml-1"  size="sm" type="select" name={`${name}.element`} defaultValue={action.element === 'static-header' ? 'header' : action.element} innerRef={register()} onChange={onSubmit} defaultValue={action.element}>
          <option className="text-dark" value="">{t('editor.question.answers_action_type')}</option>
          <option className="text-dark" value="header">{t('editor.question.answers_action_header') }</option>
          <option className="text-dark" value="statement">{t('editor.question.answers_action_statement') }</option>
          <option className="text-dark" value="clause">{t('editor.question.answers_action_clause') }</option>
          <option className="text-dark" value="text">{t('editor.question.answers_action_text') }</option>
          <option className="text-dark" value="signature" >{t('editor.question.answers_action_signature') }</option>
          <option className="text-dark" value="break">{t('editor.question.answers_action_break') }</option>
          <option className="text-dark" value="table-wrap">{t('editor.question.answers_action_table') }</option>
          <option className="text-dark" value="ol">{t('editor.question.answers_action_ol') }</option>
          <option className="text-dark" value="ul">{t('editor.question.answers_action_ul') }</option>
          <option className="text-dark" value="attachments">{t('editor.question.answers_action_attachments') }</option>
          <option className="text-dark" value="notifications" >{t('editor.question.answers_action_notifications') }</option> 
        </Input>
        <Input className="ml-1" size="sm" type="select" name={`${name}.item`}  disabled={type === ''} innerRef={register()} onChange={onSubmit} defaultValue={action.item}>
          <option className="text-dark" value="">{t('editor.question.answers_action_element')}</option>
          {/*TODO: parse text align elemtns*/}
          {!['attachments', 'notifications'].includes(type) && elements.map(element => (
              <option className="text-dark" value={element._id}>{truncate(element.text, 50)}</option>
          ))}
          {type === 'attachments' && attachments?.map(attachment => (
              <option className="text-dark" value={attachment._id} data-type="notifications">{truncate(attachment.name, 50)}</option>
          ))}
          {type === 'notifications' && doc.notifications?.map(notification => (
              <option className="text-dark" value={notification._id} data-type="notifications">{truncate(notification.name, 50)}</option>
          ))}
        
        </Input>
      <div className="answer-action-tools ml-2 text-danger pointer" onClick={handleRemove}>
          <i className="fas fa-times"></i>
      </div>
    
    </div>
  )
}